import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * AuthCallback — processes ?session_id=... in the URL fragment coming back from
 * Emergent Google Auth.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const session_id = params.get("session_id");
    if (!session_id) {
      nav("/login", { replace: true });
      return;
    }
    // Guard against React StrictMode double-invoke AND accidental re-runs of a
    // session_id that's already been redeemed (Emergent sessions are single-use).
    const guardKey = `ofr_oauth_processed:${session_id}`;
    if (sessionStorage.getItem(guardKey)) return;
    sessionStorage.setItem(guardKey, "1");

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id });
        await refresh();
        toast.success(`Welcome ${data.user?.name?.split(" ")[0] || "back"}!`);
        const role = data.user?.role;
        const dest = role === "admin" ? "/admin" : role === "builder" ? "/builder" : role === "tele_caller" ? "/telecaller" : "/";
        nav(dest, { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Unknown error";
        console.error("Google sign-in failed:", detail, e);
        toast.error(`Google sign-in failed: ${detail}. Please try again.`);
        nav("/login", { replace: true });
      }
    })();
  }, [location.hash, nav, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream">
      <div className="text-center">
        <div className="w-12 h-12 rounded-full border-4 border-forest border-t-transparent animate-spin mx-auto"></div>
        <div className="mt-4 text-sm text-forest font-medium">Signing you in with Google…</div>
      </div>
    </div>
  );
}
